'use client'

import { CSSProperties } from 'react'

const PIXEL_SPACING = 4

type CalculateOptions = {
  avoidOverlap?: boolean
  matchTargetWidth?: boolean
}

export const calculatePositionedStyle = (
  targetElement: HTMLElement,
  positionedElement: HTMLElement,
  position: 'top' | 'bottom' | 'left' | 'right' | 'bottom-right',
  offset: number = 0,
  fullWidth: boolean = false,
  options: CalculateOptions = {},
): CSSProperties => {
  const targetRect = targetElement.getBoundingClientRect()
  let elemW = positionedElement.offsetWidth
  const elemH = positionedElement.offsetHeight
  const spacing = PIXEL_SPACING + offset
  const { avoidOverlap = true, matchTargetWidth } = options

  type Pos = typeof position
  const flipMap: Record<Pos, Pos | null> = {
    top: 'bottom',
    bottom: 'top',
    left: 'right',
    right: 'left',
    'bottom-right': null,
  }

  if (matchTargetWidth) {
    elemW = Math.round(targetRect.width)
  }

  // compute the raw x/y for each position (adjacent + spacing)
  const getCoords = (pos: Pos): { x: number; y: number } => {
    switch (pos) {
      case 'top':
        return {
          x: targetRect.left + (targetRect.width - elemW) / 2,
          y: targetRect.top - elemH - (avoidOverlap ? spacing : 0),
        }
      case 'bottom':
        return {
          x: fullWidth ? targetRect.left : targetRect.left + (targetRect.width - elemW) / 2,
          y: targetRect.bottom + (avoidOverlap ? spacing : 0),
        }
      case 'left':
        return {
          x: targetRect.left - elemW - (avoidOverlap ? spacing : 0),
          y: targetRect.top + (targetRect.height - elemH) / 2,
        }
      case 'right':
        return {
          x: targetRect.right + (avoidOverlap ? spacing : 0),
          y: targetRect.top + (targetRect.height - elemH) / 2,
        }
      case 'bottom-right':
        return {
          x: targetRect.right - elemW,
          y: targetRect.bottom + (avoidOverlap ? spacing : 0),
        }
    }
  }

  // test whether x/y would go outside your viewport margins
  const isOverflow = ({ x, y }: { x: number; y: number }) =>
    x < PIXEL_SPACING ||
    y < PIXEL_SPACING ||
    x + elemW > window.innerWidth - PIXEL_SPACING ||
    y + elemH > window.innerHeight - PIXEL_SPACING

  // clamp into the screen if we have to
  const clamp = ({ x, y }: { x: number; y: number }) => ({
    x: Math.min(Math.max(x, PIXEL_SPACING), window.innerWidth - elemW - PIXEL_SPACING),
    y: Math.min(Math.max(y, PIXEL_SPACING), window.innerHeight - elemH - PIXEL_SPACING),
  })

  // 1) try your desired spot
  let coords = getCoords(position)

  // 2) if avoidOverlap AND it overflows, try the flipped side
  if (avoidOverlap && isOverflow(coords)) {
    const flipped = flipMap[position]
    if (flipped) {
      const alt = getCoords(flipped)
      if (!isOverflow(alt)) coords = alt
    }
  }

  // 3) final clamp fallback
  coords = clamp(coords)

  const styles: CSSProperties = {
    position: 'fixed',
    left: Math.round(coords.x),
    top: Math.round(coords.y),
  }

  // preserve your fullWidth behaviour on bottom
  if ((fullWidth && position === 'bottom') || matchTargetWidth) {
    styles.width = Math.round(targetRect.width)
  }

  return styles
}
